import { ALBEvent, ALBResult, Context } from 'aws-lambda';
import { api, GetAPI, PostAPI } from '../../api/API';
import { ErrorReporter } from '../../common/ErrorReporter';
import { ReplicantError, ReplicantErrorCode, ReplicantErrorSubCode } from '../../Errors';
import logger from '../../logger';
import SB from '../../SchemaBuilder';
import { APIError } from '../../server/APIError';
import { stripFields } from '../../utils/ObjUtils';
import { errorReqContextFromLambda } from './LambdaErrorContext';
import Metrics from '../../server/Metrics';

const statusDescriptions = {
    200: 'OK',
    204: 'No Content',
    307: 'Temporary Redirect',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    409: 'Conflict',
    500: 'Internal Server Error',
};

const errorCodeToStatusCodeMap: { [key in ReplicantErrorCode]?: number } = {
    [ReplicantErrorCode.authorization_error]: 401,
    [ReplicantErrorCode.session_desync_error]: 409,
    [ReplicantErrorCode.version_error]: 409,
};

const errorSubCodeToStatusCodeMap: { [key in ReplicantErrorSubCode]?: number } = {
    [ReplicantErrorSubCode.action_args_too_large]: 400,
    [ReplicantErrorSubCode.analytics_extra_properties_error]: 400,
    [ReplicantErrorSubCode.invalid_action]: 400,
    [ReplicantErrorSubCode.client_time_invalid]: 400,
    [ReplicantErrorSubCode.client_time_offset_too_large]: 400,
    [ReplicantErrorSubCode.random_seed_invalid]: 409,
    [ReplicantErrorSubCode.retry_queue_mismatch]: 409,
    [ReplicantErrorSubCode.rev_id_mismatch]: 409,
};

function getStatusCodeFromError(error: ReplicantError): number {
    if (error.subCode && error.subCode in errorSubCodeToStatusCodeMap) {
        return errorSubCodeToStatusCodeMap[error.subCode]!;
    }

    if (error.code in errorCodeToStatusCodeMap) {
        return errorCodeToStatusCodeMap[error.code]!;
    }

    return 500;
}

function isCodeWithStatusDescription(statusCode: number): statusCode is keyof typeof statusDescriptions {
    return statusCode in statusDescriptions;
}

type Matcher = (opts: { method: string; url: string }) => boolean;

type Payload<T extends number> = { statusCode: T } & (T extends keyof typeof statusDescriptions
    ? Omit<ALBResult, 'isBase64Encoded' | 'statusCode' | 'statusDescription'> & { statusDescription?: string }
    : Omit<ALBResult, 'isBase64Encoded' | 'statusCode'>);

type Input<TBody = undefined> = {
    event: ALBEvent;
    context: Context;
    respond: <T extends number>(opts: { payload: Payload<T>; gzip?: boolean }) => ALBResult;
    body: TBody;
};

export type Handler<TInput, TOutput = ALBResult> = (input: TInput) => Promise<TOutput>;

export type Middleware<TBody> = (input: Input<TBody>) => Promise<ALBResult | null> | null;

type Route =
    | {
          matcher: Matcher;
          handler: Handler<Input<any>>;
      }
    | {
          matcher: Matcher;
          handler: Handler<Input<any>>;
          middlewares: Middleware<any>[];
          bodyParser: (evemt: ALBEvent) => any;
      };

const corsHeaders = {
    'Access-Control-Allow-Origin': '*', // in PROD needs to be strigent.
    'Access-Control-Allow-Methods': 'POST, GET',
    'Access-Control-Allow-Headers': 'origin, content-type, accept, authentication',
};

export default class Router {
    private routes: Route[] = [];

    constructor(
        private options: {
            metrics: Metrics;
            errorReporter: ErrorReporter;
            gzipToBase64: (body: string) => { body: string; byteLength: number };
        },
    ) {
        //
    }

    get<TUrl extends keyof GetAPI>(url: TUrl, handler: Handler<Input<GetAPI[TUrl]>>): void;
    get<TUrl extends keyof GetAPI>(
        url: TUrl,
        middlewares: Middleware<GetAPI[TUrl]>[],
        handler: Handler<Input<GetAPI[TUrl]>>,
    ): void;
    get<TUrl extends keyof GetAPI>(
        url: TUrl,
        middlewaresOrHandler: Middleware<GetAPI[TUrl]>[] | Handler<Input<GetAPI[TUrl]>>,
        maybeHandler?: Handler<Input<GetAPI[TUrl]>>,
    ): void {
        this.routes.push({
            matcher: (opts) => opts.method === 'GET' && opts.url === url,
            handler: maybeHandler ?? (middlewaresOrHandler as Handler<Input<GetAPI[TUrl]>>),
            middlewares: maybeHandler ? (middlewaresOrHandler as Middleware<GetAPI[TUrl]>[]) : [],
            bodyParser: (event) => {
                const schema = api[url].get.schema;
                if (!schema) {
                    return null;
                }

                const query = event.queryStringParameters!;

                const body = Object.entries(query).reduce((a, [key, value]) => {
                    if (!value) {
                        return a;
                    }

                    // If we expect a string, return a string.
                    const keySchema = (schema as SB.ObjectSchema<any>).getSchema(key);
                    if (keySchema instanceof SB.StringSchema || keySchema instanceof SB.TupleSchema) {
                        return { ...a, [key]: decodeURIComponent(value) };
                    }

                    // Everything else needs to be parsed.
                    return { ...a, [key]: JSON.parse(decodeURIComponent(value)) };
                }, {});

                schema.tryValidate(body);

                return body;
            },
        });
    }

    post<TUrl extends keyof PostAPI>(url: TUrl, handler: Handler<Input<PostAPI[TUrl]>>): void;
    post<TUrl extends keyof PostAPI>(
        url: TUrl,
        middlewares: Middleware<PostAPI[TUrl]>[],
        handler: Handler<Input<PostAPI[TUrl]>>,
    ): void;
    post<TUrl extends keyof PostAPI>(
        url: TUrl,
        middlewaresOrHandler: Middleware<PostAPI[TUrl]>[] | Handler<Input<PostAPI[TUrl]>>,
        maybeHandler?: Handler<Input<PostAPI[TUrl]>>,
    ): void {
        this.routes.push({
            matcher: (opts) => opts.method === 'POST' && opts.url === url,
            handler: maybeHandler ?? (middlewaresOrHandler as Handler<Input<PostAPI[TUrl]>>),
            bodyParser: (event) => {
                const schema = api[url].post.schema;
                if (!schema) {
                    return null;
                }

                const body = JSON.parse(event.body!);
                schema.tryValidate(body);

                return body;
            },
            middlewares: maybeHandler ? (middlewaresOrHandler as Middleware<PostAPI[TUrl]>[]) : [],
        });
    }

    listen() {
        return async (event: ALBEvent, context: Context): Promise<ALBResult> => {
            const eventPath: string = event.path;
            const method: string = event.httpMethod;

            const requestStartTime = Date.now();

            const respondWithPayload = this.createRespond({ requestStartTime });

            if (!eventPath) {
                return respondWithPayload({
                    payload: {
                        statusCode: 400,
                        body: 'Invalid path',
                    },
                });
            }

            if (method === 'OPTIONS') {
                return respondWithPayload({
                    payload: {
                        statusCode: 204,
                        body: '',
                    },
                });
            }

            const urlMatches = eventPath.match(/\/(v.+?|latest)\/(.*)/);
            if (!urlMatches || urlMatches.length < 2) {
                return respondWithPayload({
                    payload: {
                        statusCode: 404,
                        body: JSON.stringify({ message: 'Not found' }),
                    },
                });
            }

            this.logRequest(event);

            const url = urlMatches[2]!;

            const route = this.routes.find(({ matcher }) => matcher({ url, method }));

            if (route) {
                const metricKey = `${method}-${url}`;
                const respond: Input['respond'] = (opts) => respondWithPayload({ metricKey, ...opts });

                this.options.errorReporter.setErrorContext?.(errorReqContextFromLambda(event, context));

                let body;

                if ('bodyParser' in route) {
                    try {
                        body = route.bodyParser?.(event);
                    } catch (e) {
                        logger.error(metricKey, ' Error parsing arguments: ', e);

                        return respond({
                            payload: {
                                statusCode: 400,
                                body: 'Invalid parameters',
                            },
                        });
                    }

                    for (const middleware of route.middlewares) {
                        const result = await middleware({ body, event, context, respond });
                        if (result) {
                            return result;
                        }
                    }
                }

                try {
                    return await route.handler({
                        body,
                        event,
                        context,
                        respond: (opts) => respond({ ...opts }),
                    });
                } catch (e: any) {
                    if (e instanceof APIError) {
                        return e.status
                            ? respond({ payload: { ...e.status, body: e.message }, gzip: true })
                            : respond({ payload: { statusCode: 400, body: e.message }, gzip: true });
                    }

                    await this.options.errorReporter.captureException(e);

                    let isProd = false;
                    try {
                        isProd = process.env.STAGE === 'prod';
                    } catch {
                        isProd = false;
                    }

                    const statusCode = e instanceof ReplicantError ? getStatusCodeFromError(e) : 500;

                    if (isProd) {
                        return respond({
                            payload: {
                                statusCode,
                                body: JSON.stringify({
                                    message: context.awsRequestId,
                                    code: e.code,
                                    subCode: e.subCode,
                                    clientExtras: e instanceof ReplicantError ? e.clientExtras : undefined,
                                }),
                            },
                        });
                    }

                    return respond({
                        payload: {
                            statusCode,
                            body: JSON.stringify({
                                message: e.message,
                                code: e.code,
                                subCode: e.subCode,
                                clientExtras: e instanceof ReplicantError ? e.clientExtras : undefined,

                                reqId: context.awsRequestId,
                                context: JSON.stringify(stripFields(body, ['auth', 'dataUrl'])),
                            }),
                        },
                    });
                }
            }

            return respondWithPayload({
                metricKey: 'notfound',
                payload: {
                    statusCode: 404,
                    body: JSON.stringify({ message: 'Not found' }),
                },
            });
        };
    }

    //

    private createRespond(createOpts: { requestStartTime: number }) {
        return <T extends number>(opts: { payload: Payload<T>; metricKey?: string; gzip?: boolean }) => {
            const { requestStartTime } = createOpts;
            const { gzip, metricKey } = opts;

            const statusCode = opts.payload.statusCode;
            const statusDescription = isCodeWithStatusDescription(statusCode)
                ? opts.payload.statusDescription || statusDescriptions[statusCode]
                : opts.payload.statusDescription;

            let payload: ALBResult = {
                ...opts.payload,
                statusDescription,
                isBase64Encoded: false,
                headers: {
                    ...corsHeaders,
                    ...opts.payload.headers,
                },
            };

            let bytes = 0;

            if (gzip) {
                // need to gzip payload and add headers.
                const { body, byteLength } = payload.body
                    ? this.options.gzipToBase64(payload.body)
                    : { body: undefined, byteLength: 0 };
                bytes = byteLength;

                payload = {
                    ...payload,
                    body,
                    isBase64Encoded: true,
                    headers: {
                        'Content-Encoding': 'gzip',
                        'Content-Type': 'application/json',
                        ...payload.headers,
                    },
                };
            } else {
                bytes = payload.body?.length || 0;
            }

            if (metricKey) {
                const serviceTime = Date.now() - requestStartTime;

                this.options.metrics.pushRequestMetric({
                    endpoint: metricKey,
                    statusCode: payload.statusCode,
                    bytes,
                    serviceTime,
                });
            }

            return payload;
        };
    }

    private logRequest(event: ALBEvent) {
        // Log request.
        let parsedBody: { [key: string]: any } | string = '';
        try {
            if (event.body) {
                parsedBody = JSON.parse(event.body);
            } else if (event.queryStringParameters) {
                parsedBody = { ...event.queryStringParameters };
                for (const key in parsedBody) {
                    parsedBody[key] = JSON.parse(parsedBody[key]);
                }
            }
        } catch (e) {
            parsedBody = event.body || '';
        }

        logger.debug(`${event.httpMethod} ${event.path}`, stripFields(parsedBody, ['dataUrl']));
    }
}
